import { z } from 'zod'

import {
  CommentDeletePolicyEnum,
  CommentEditPolicyEnum,
  DEFAULT_COMMENT_DELETE_POLICY,
  DEFAULT_COMMENT_EDIT_POLICY,
} from './comment-moderation'
import { RevenueTier, UtilizationMetric } from './member-resource'
import { ModuleTypeEnum } from './module'
import { AccountType } from './organization'

// Time format options
export enum TimeFormatEnum {
  DECIMAL = 'DECIMAL', // 2.5 hours
  HH_MM = 'HH_MM', // 2:30
}

// Date format options
export enum DateFormatEnum {
  MM_DD_YYYY = 'MM/DD/YYYY',
  DD_MM_YYYY = 'DD/MM/YYYY',
  YYYY_MM_DD = 'YYYY-MM-DD',
  MMM_DD_YYYY = 'MMM DD, YYYY',
  DD_MMM_YYYY = 'DD MMM YYYY',
}

// First day of week options
export enum FirstDayOfWeekEnum {
  SUNDAY = 0,
  MONDAY = 1,
  SATURDAY = 6,
}

// Story points format options
export enum StoryPointsFormatEnum {
  FIBONACCI = 'FIBONACCI', // 1, 2, 3, 5, 8, 13, 21
  T_SHIRT = 'T_SHIRT', // XS, S, M, L, XL, XXL
}

// English spelling variant — used by AI-generated content (meeting summaries,
// etc.) and available anywhere org copy should follow a regional convention.
export enum EnglishVariantEnum {
  US = 'US', // American: "prioritize", "color", "organize"
  UK = 'UK', // British: "prioritise", "colour", "organise"
}

/** Read the org's English variant from its settings JSON (defaults to US). */
export function getEnglishVariant(settings: unknown): EnglishVariantEnum {
  const general = (settings as { general?: { englishVariant?: unknown } })
    ?.general
  return general?.englishVariant === EnglishVariantEnum.UK
    ? EnglishVariantEnum.UK
    : EnglishVariantEnum.US
}

// T-shirt size mapping for story points
export const T_SHIRT_SIZES = [
  { size: 'XS', value: 1, label: 'XS' },
  { size: 'S', value: 2, label: 'S' },
  { size: 'M', value: 3, label: 'M' },
  { size: 'L', value: 5, label: 'L' },
  { size: 'XL', value: 8, label: 'XL' },
  { size: 'XXL', value: 13, label: 'XXL' },
] as const

export type TShirtSize = (typeof T_SHIRT_SIZES)[number]['size']

// Entity terminology customization
export interface EntityTerminology {
  singular: string
  plural: string
}

export enum SupplierTerminologyMode {
  SUPPLIER = 'SUPPLIER',
  VENDOR = 'VENDOR',
}

// Organization-level settings schema
export const OrganizationGeneralSettingsSchema = z.object({
  timeFormat: z.nativeEnum(TimeFormatEnum).default(TimeFormatEnum.DECIMAL),
  dateFormat: z.nativeEnum(DateFormatEnum).default(DateFormatEnum.MM_DD_YYYY),
  firstDayOfWeek: z
    .nativeEnum(FirstDayOfWeekEnum)
    .default(FirstDayOfWeekEnum.SUNDAY),
  timezone: z.string().default('UTC'),
  currency: z.string().default('USD'),
  language: z.string().default('en'),
  englishVariant: z
    .nativeEnum(EnglishVariantEnum)
    .default(EnglishVariantEnum.US),
  fiscalYearStartMonth: z.number().int().min(0).max(11).default(3),
  // Which allocation basis feeds forecast cost/profit figures in Financials:
  // 'confirmed' = committed work only; 'all_planned' = every planned
  // allocation (confirmed + tentative + placeholder).
  forecastCostBasis: z.enum(['confirmed', 'all_planned']).default('confirmed'),
  entityTerminology: z
    .object({
      singular: z.string().default('client'),
      plural: z.string().default('clients'),
    })
    .default({ singular: 'client', plural: 'clients' }),
  onboardingDismissed: z.boolean().default(false),
  // Comment moderation (Admin > Settings > Comment Moderation). `.catch` keeps
  // a bad stored value from breaking every consumer that parses general.
  commentEditPolicy: z
    .nativeEnum(CommentEditPolicyEnum)
    .catch(DEFAULT_COMMENT_EDIT_POLICY),
  commentDeletePolicy: z
    .nativeEnum(CommentDeletePolicyEnum)
    .catch(DEFAULT_COMMENT_DELETE_POLICY),
})

export type OrganizationGeneralSettings = z.infer<
  typeof OrganizationGeneralSettingsSchema
>

// Pay period type enum for settings
export enum PayPeriodTypeSettingEnum {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  SEMIMONTHLY = 'SEMIMONTHLY',
  MONTHLY = 'MONTHLY',
}

export enum ContractorInvoiceEligibilityPolicyEnum {
  TIME_APPROVED_ONLY = 'TIME_APPROVED_ONLY',
  SUBMITTED_TIME_ALLOWED = 'SUBMITTED_TIME_ALLOWED',
}

export enum BillingConfigurationEnforcementModeEnum {
  WARN_ONLY = 'WARN_ONLY',
  SUBMIT_BLOCK = 'SUBMIT_BLOCK',
  HARD_BLOCK = 'HARD_BLOCK',
}

// Controls how overlapping (double-tracked) time entries are handled.
export enum OverlapEnforcementModeEnum {
  OFF = 'OFF', // Ignore overlaps entirely
  WARN = 'WARN', // Allow overlaps but surface a warning
  HARD_BLOCK = 'HARD_BLOCK', // Reject entries that overlap an existing one
}

export const BILLING_CONFIGURATION_ELIGIBLE_BILL_TO_TYPES = [
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.TASK_GROUP,
  ModuleTypeEnum.TASK,
  'SUBTASK',
  ModuleTypeEnum.EVENT,
] as const

export const TIME_ENTRY_SUPPORTED_BILL_TO_TYPES = [
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.TASK_GROUP,
  ModuleTypeEnum.TASK,
  ModuleTypeEnum.TICKET,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.CONTACT,
  ModuleTypeEnum.EVENT,
  ModuleTypeEnum.CONTRACT,
  ModuleTypeEnum.INVOICE,
  ModuleTypeEnum.INVENTORY,
  ModuleTypeEnum.SUPPLIER,
  ModuleTypeEnum.DOCUMENT,
  ModuleTypeEnum.NOTE,
  ModuleTypeEnum.CUSTOM_COLLECTION,
  ModuleTypeEnum.ORGANIZATION,
  'SUBTASK',
] as const

// Time entry module settings
export const TimeEntryModuleSettingsSchema = z.object({
  // Basic settings
  defaultBillable: z.boolean().default(true),
  // Internal work (logged to the organization itself or without a client) is
  // billable by default only when this is on.
  internalTimeBillable: z.boolean().default(false),
  allowManualEntry: z.boolean().default(true),
  allowTimerEntry: z.boolean().default(true),
  requireDescription: z.boolean().default(false),
  requireModuleLink: z.boolean().default(false),
  allowedBillToTypes: z
    .array(z.string())
    .default([...TIME_ENTRY_SUPPORTED_BILL_TO_TYPES]),
  minimumDuration: z.number().default(0), // Minimum duration in minutes
  roundingInterval: z.number().default(1), // Round to nearest X minutes (0 = no rounding)
  // Double-tracking control: how to handle a new/edited entry whose time window
  // overlaps an existing entry for the same user. Only entries with both a start
  // and end time participate; duration-only entries are never flagged.
  overlapEnforcementMode: z
    .nativeEnum(OverlapEnforcementModeEnum)
    .default(OverlapEnforcementModeEnum.HARD_BLOCK),
  allowFutureEntries: z.boolean().default(true),
  maxHoursPerEntry: z.number().default(24),
  defaultStatus: z.string().default('DRAFT'),

  // Pay period configuration
  autoCreatePayPeriods: z.boolean().default(true), // Auto-create pay periods when none exists for current date
  payPeriodType: z
    .nativeEnum(PayPeriodTypeSettingEnum)
    .default(PayPeriodTypeSettingEnum.WEEKLY),
  payPeriodStartDay: z.number().min(0).max(6).default(1), // 0=Sun, 1=Mon, 2=Tue, etc.
  firstPayPeriodDate: z.string().optional(), // ISO date for biweekly alignment
  semiMonthlyFirstDay: z.number().min(1).max(28).default(1), // 1st of month
  semiMonthlySecondDay: z.number().min(1).max(28).default(16), // 16th of month

  // Approval workflow settings
  requireApproval: z.boolean().default(true),
  approvalDeadlineDays: z.number().min(0).default(3), // Days after period closes
  autoSubmitOnPeriodClose: z.boolean().default(false),
  allowEditAfterApproval: z.boolean().default(true), // Requires re-approval
  lockEntriesOnPeriodClose: z.boolean().default(true),
  blockNewEntriesAfterSubmit: z.boolean().default(false),

  // Approver configuration
  projectOwnersCanApprove: z.boolean().default(false),
  managersCanApprove: z.boolean().default(true), // Requires org chart

  // How contractors and freelancers are paid unless their own pay settings
  // say otherwise. Hourly bases only; a retainer needs a per-person amount.
  contractorPayBasis: z
    .enum(['ALL_HOURS', 'BILLABLE_HOURS'])
    .default('BILLABLE_HOURS'),

  // Contractor invoice compliance settings
  contractorInvoiceEligibilityPolicy: z
    .nativeEnum(ContractorInvoiceEligibilityPolicyEnum)
    .default(ContractorInvoiceEligibilityPolicyEnum.TIME_APPROVED_ONLY),
  contractorInvoiceRecipientMemberIds: z.array(z.string().uuid()).default([]),
  contractorInvoiceRecipientEmails: z.array(z.string()).default([]),
  // When enabled, contractor invoices are generated and emailed automatically:
  // on time approval if requireApproval is true, otherwise at pay period end.
  autoSubmitContractorInvoices: z.boolean().default(false),
  // Grace period (in days) for the no-approval period-end path: invoices are
  // only auto-sent once the pay period has been over for this many days, giving
  // contractors time to finish logging late entries. 0 = send as soon as the
  // period ends.
  autoSubmitContractorInvoiceDelayDays: z.number().min(0).default(0),
  // Server-managed marker stamped when autoSubmitContractorInvoices is turned
  // on. The period-end cron only auto-invoices periods ending on/after this
  // moment, so newly enabling the feature never backfills historical periods.
  // Not user-editable.
  autoSubmitContractorInvoicesEnabledAt: z.string().nullable().default(null),

  // Expected hours
  expectedWeeklyHours: z.number().min(0).default(40), // Default expected hours per week
  warnBelowExpectedHours: z.boolean().default(true),
  expectedHoursWarningThreshold: z.number().min(0).max(100).default(80), // Warn if below X% of expected

  // Notifications
  notifyOnSubmission: z.boolean().default(true),
  notifyOnApproval: z.boolean().default(true),
  notifyOnRejection: z.boolean().default(true),
  reminderDaysBeforeClose: z.number().min(0).default(2),
  sendMissingTimesheetReminders: z.boolean().default(true),
})

export type TimeEntryModuleSettings = z.infer<
  typeof TimeEntryModuleSettingsSchema
>

// Task module settings
export const TaskModuleSettingsSchema = z.object({
  defaultPriority: z.string().default('MEDIUM'),
  requireDueDate: z.boolean().default(false),
  enableRecurring: z.boolean().default(true),
  defaultView: z.enum(['list', 'board', 'calendar']).default('list'),
  enableSprints: z.boolean().default(false),
  enableStoryPoints: z.boolean().default(false),
  storyPointsFormat: z
    .nativeEnum(StoryPointsFormatEnum)
    .default(StoryPointsFormatEnum.FIBONACCI),
})

export type TaskModuleSettings = z.infer<typeof TaskModuleSettingsSchema>

// Project module settings
export const ProjectModuleSettingsSchema = z.object({
  requireProjectCode: z.boolean().default(false),
  projectCodePrefix: z.string().default(''),
  enableTimelines: z.boolean().default(true),
  defaultProjectStatus: z.string().default('ACTIVE'),
  // Default status templates for different project types
  defaultStatusTemplates: z
    .object({
      basic: z.string().optional(), // Default status template ID for basic projects
      scrum: z.string().optional(), // Default status template ID for scrum projects
      kanban: z.string().optional(), // Default status template ID for kanban projects
      waterfall: z.string().optional(), // Default status template ID for waterfall projects
    })
    .default({}),
})

export type ProjectModuleSettings = z.infer<typeof ProjectModuleSettingsSchema>

// Resource module settings
export const ResourceModuleSettingsSchema = z.object({
  // Utilization targets
  utilization: z
    .object({
      targetUtilization: z.number().min(0).max(100).default(80), // Target % of hours that should be utilized
      targetBillable: z.number().min(0).max(100).default(70), // Target % of utilized hours that should be billable
    })
    .default({ targetUtilization: 80, targetBillable: 70 }),

  // Capacity defaults by employment type
  capacity: z
    .object({
      fullTimeHours: z.number().min(1).max(168).default(40), // Weekly hours for full-time employees
      partTimeHours: z.number().min(1).max(168).default(20), // Weekly hours for part-time employees
      contractorHours: z.number().min(1).max(168).default(40), // Weekly hours for contractors
      freelancerHours: z.number().min(1).max(168).default(20), // Weekly hours for freelancers
    })
    .default({
      fullTimeHours: 40,
      partTimeHours: 20,
      contractorHours: 40,
      freelancerHours: 20,
    }),

  // Default rates
  rates: z
    .object({
      defaultBillRate: z.number().min(0).default(150), // Default bill rate per hour
      defaultCostRate: z.number().min(0).default(75), // Default cost rate per hour
      defaultPayRate: z.number().min(0).default(50), // Default pay rate per hour
      currency: z.string().default('USD'), // Default currency for rates
    })
    .default({
      defaultBillRate: 150,
      defaultCostRate: 75,
      defaultPayRate: 50,
      currency: 'USD',
    }),

  // Billing role/configuration enforcement
  billingConfiguration: z
    .object({
      enforceBillingConfiguration: z.boolean().default(false),
      enforcementMode: z
        .nativeEnum(BillingConfigurationEnforcementModeEnum)
        .default(BillingConfigurationEnforcementModeEnum.WARN_ONLY),
      billToTypes: z
        .array(z.string())
        .default([...BILLING_CONFIGURATION_ELIGIBLE_BILL_TO_TYPES]),
    })
    .default({
      enforceBillingConfiguration: false,
      enforcementMode: BillingConfigurationEnforcementModeEnum.WARN_ONLY,
      billToTypes: [...BILLING_CONFIGURATION_ELIGIBLE_BILL_TO_TYPES],
    }),

  // Allocation policies
  allocation: z
    .object({
      maxAllocationPercent: z.number().min(100).max(200).default(100), // Max allocation % per member
      allowOverbooking: z.boolean().default(false), // Allow allocations exceeding 100%
      conflictWarningThreshold: z.number().min(0).max(100).default(90), // Warn when allocation exceeds this %
      requireApproval: z.boolean().default(false), // Require approval for new allocations

      // Plan 4: Allocation lifecycle / state policy
      defaultAllocationStatus: z
        .enum(['TENTATIVE', 'CONFIRMED'])
        .default('TENTATIVE'),
      requireConfirmationForPlaceholderAssignment: z.boolean().default(true),
      autoArchivePastAllocations: z.boolean().default(true),
      autoArchiveGraceDays: z.number().int().min(0).default(3),
      demandCancellationReviewDays: z.number().int().min(0).default(7),
      defaultMaxProjectCount: z.number().int().min(1).nullable().default(null),
    })
    .default({
      maxAllocationPercent: 100,
      allowOverbooking: false,
      conflictWarningThreshold: 90,
      requireApproval: false,
      defaultAllocationStatus: 'TENTATIVE',
      requireConfirmationForPlaceholderAssignment: true,
      autoArchivePastAllocations: true,
      autoArchiveGraceDays: 3,
      demandCancellationReviewDays: 7,
      defaultMaxProjectCount: null,
    }),

  // Skills settings
  skills: z
    .object({
      enableCertifications: z.boolean().default(true), // Allow certification tracking
      enableProficiencyLevels: z.boolean().default(true), // Enable 1-5 proficiency scale
      requireSkillApproval: z.boolean().default(false), // Require manager approval for skill claims
    })
    .default({
      enableCertifications: true,
      enableProficiencyLevels: true,
      requireSkillApproval: false,
    }),

  // Budget settings
  budget: z
    .object({
      defaultMarginTarget: z.number().min(0).max(100).default(30), // Default margin target %
      budgetApprovalThreshold: z.number().min(0).default(0), // Auto-approve budgets below this amount (0 = always require)
      requireApprovalAbove: z.number().min(0).default(100000), // Require Finance approval above this amount
    })
    .default({
      defaultMarginTarget: 30,
      budgetApprovalThreshold: 0,
      requireApprovalAbove: 100000,
    }),

  // Plan 9a: utilization definitions — feeds metric selector, burnout detection,
  // revenue-target derivation, staleness thresholds, and the Revenue/Profit tier.
  utilizationDefinitions: z
    .object({
      primaryUtilizationMetric: z
        .nativeEnum(UtilizationMetric)
        .default(UtilizationMetric.BILLABLE),
      marginThreshold: z.number().min(0).max(100).default(25),
      revenueTargetMultiplier: z.number().min(0).nullable().default(null),
      burnoutStreakThreshold: z.number().int().min(1).default(4),
      stalenessThreshold: z.number().int().min(1).default(24), // hours
      nightlyRecalcTime: z.string().default('02:00'),
      burnoutNotificationsEnabled: z.boolean().default(true),
      // TimeCategory ids whose hours count as productive. Empty = productive
      // hours equal billable hours. Values saved before time categories were
      // free-text names; the API maps those to categories by name at read time.
      productiveCategories: z.array(z.string()).default([]),
      revenueTier: z
        .nativeEnum(RevenueTier)
        .default(RevenueTier.FINANCIAL_ACTUALS),
    })
    .default({
      primaryUtilizationMetric: UtilizationMetric.BILLABLE,
      marginThreshold: 25,
      revenueTargetMultiplier: null,
      burnoutStreakThreshold: 4,
      stalenessThreshold: 24,
      nightlyRecalcTime: '02:00',
      burnoutNotificationsEnabled: true,
      productiveCategories: [],
      revenueTier: RevenueTier.FINANCIAL_ACTUALS,
    }),
})

export type UtilizationDefinitionsSettings = z.infer<
  typeof ResourceModuleSettingsSchema
>['utilizationDefinitions']

export type ResourceModuleSettings = z.infer<
  typeof ResourceModuleSettingsSchema
>

// Entity module settings
export const EntityModuleSettingsSchema = z.object({
  enableEntityTypes: z.boolean().default(false),
  defaultEntityTypeId: z.string().uuid().optional(),
  defaultStatus: z.string().default('ACTIVE'),
})

export type EntityModuleSettings = z.infer<typeof EntityModuleSettingsSchema>

export const ExpenseModuleSettingsSchema = z.object({
  // Approval-first by default: submitted expenses wait for an approver
  // unless the org explicitly turns this off in Expenses → Settings.
  requireApproval: z.boolean().default(true),
  defaultCurrency: z.string().default('USD'),
  allowCompanyPaid: z.boolean().default(true),
  allowReimbursements: z.boolean().default(true),
  receiptRequiredAboveAmount: z.number().min(0).default(0),
  paymentMethods: z
    .array(z.string().trim().min(1))
    .default(['Corporate card', 'Personal card', 'Cash', 'Bank transfer']),
})

export type ExpenseModuleSettings = z.infer<typeof ExpenseModuleSettingsSchema>

export const SupplierModuleSettingsSchema = z.object({
  terminology: z
    .nativeEnum(SupplierTerminologyMode)
    .default(SupplierTerminologyMode.VENDOR),
})

export type SupplierModuleSettings = z.infer<
  typeof SupplierModuleSettingsSchema
>

// Inventory module settings — approval workflow and GL posting configuration.
export const InventoryModuleSettingsSchema = z.object({
  // When true, adjustments whose |delta| exceeds the threshold are held in
  // PENDING until a MANAGE-permission user (not the creator) approves them.
  requireAdjustmentApproval: z.boolean().default(false),
  adjustmentApprovalThreshold: z.number().int().min(0).default(0),

  // When true, the server posts journal entries to the org chart of accounts
  // for inventory movements. Missing account codes skip posting with a
  // warning instead of failing the movement.
  glPostingEnabled: z.boolean().default(false),

  // Fallback account codes used when an item or category doesn't specify.
  defaultInventoryAssetAccount: z.string().optional(),
  defaultCogsAccount: z.string().optional(),
  defaultInventoryAdjustmentExpenseAccount: z.string().optional(),
})

export type InventoryModuleSettings = z.infer<
  typeof InventoryModuleSettingsSchema
>

// Client portal module settings — agency-level portal branding.
// All fields are optional; nulls signal "fall back to defaults".
export const ClientPortalModuleSettingsSchema = z.object({
  logoUrl: z.string().nullable().optional(),
  accentColor: z
    .string()
    .regex(/^#([0-9a-fA-F]{6})$/)
    .nullable()
    .optional(),
  sidebarColor: z
    .string()
    .regex(/^#([0-9a-fA-F]{6})$/)
    .nullable()
    .optional(),
  secondaryColor: z
    .string()
    .regex(/^#([0-9a-fA-F]{6})$/)
    .nullable()
    .optional(),
  coverImageUrl: z.string().nullable().optional(),
  // Focal point for the cover image, expressed as a CSS object-position value
  // ("x% y%" with x,y in [0,100]). Defaults to "50% 50%" when null/undefined.
  coverImagePosition: z
    .string()
    .regex(/^(\d{1,3}(\.\d+)?)%\s+(\d{1,3}(\.\d+)?)%$/)
    .nullable()
    .optional(),
})

export type ClientPortalModuleSettings = z.infer<
  typeof ClientPortalModuleSettingsSchema
>

// Combined module settings type
export interface ModuleSettings {
  [ModuleTypeEnum.TIME_ENTRY]?: TimeEntryModuleSettings
  [ModuleTypeEnum.TASK]?: TaskModuleSettings
  [ModuleTypeEnum.PROJECT]?: ProjectModuleSettings
  [ModuleTypeEnum.TEAM]?: ResourceModuleSettings
  [ModuleTypeEnum.ENTITY]?: EntityModuleSettings
  [ModuleTypeEnum.EXPENSE]?: ExpenseModuleSettings
  [ModuleTypeEnum.SUPPLIER]?: SupplierModuleSettings
  [ModuleTypeEnum.INVENTORY]?: InventoryModuleSettings
  CLIENT_PORTAL?: ClientPortalModuleSettings
  // Add more module settings as needed
}

// Complete organization settings
export interface OrganizationSettings {
  general: OrganizationGeneralSettings
  modules: ModuleSettings
}

// Default settings factory.
//
// Passing accountType: PERSONAL returns the Personal-account variant — People
// terminology (person/people) and entity types enabled out of the box. The
// no-arg / ORGANIZATION call is byte-identical to the historical default, so
// existing callers are unaffected.
export function getDefaultOrganizationSettings(
  accountType: AccountType = AccountType.ORGANIZATION,
): OrganizationSettings {
  const settings: OrganizationSettings = {
    general: OrganizationGeneralSettingsSchema.parse({}),
    modules: {
      [ModuleTypeEnum.TIME_ENTRY]: TimeEntryModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.TASK]: TaskModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.PROJECT]: ProjectModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.TEAM]: ResourceModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.ENTITY]: EntityModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.EXPENSE]: ExpenseModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.SUPPLIER]: SupplierModuleSettingsSchema.parse({}),
      [ModuleTypeEnum.INVENTORY]: InventoryModuleSettingsSchema.parse({}),
    },
  }

  if (accountType === AccountType.PERSONAL) {
    // People, not clients — entities are repurposed as family/friends/household.
    settings.general.entityTerminology = {
      singular: 'person',
      plural: 'people',
    }
    const entitySettings = settings.modules[ModuleTypeEnum.ENTITY]
    if (entitySettings) {
      entitySettings.enableEntityTypes = true
    }
  }

  return settings
}

// Get default resource module settings
export function getDefaultResourceSettings(): ResourceModuleSettings {
  return ResourceModuleSettingsSchema.parse({})
}

// System preference keys for organization settings
export const ORGANIZATION_SETTINGS_KEYS = {
  GENERAL: 'organization.settings.general',
  MODULE_TIME_ENTRY: 'organization.settings.module.time_entry',
  MODULE_TASK: 'organization.settings.module.task',
  MODULE_PROJECT: 'organization.settings.module.project',
  MODULE_ENTITY: 'entity.settings',
  MODULE_EXPENSE: 'organization.settings.module.expense',
  MODULE_SUPPLIER: 'organization.settings.module.supplier',
  MODULE_INVENTORY: 'organization.settings.module.inventory',
  // Plan 4: resources module settings (allocation policy)
  MODULE_RESOURCES: 'organization.settings.module.resources',
  MODULE_CLIENT_PORTAL: 'organization.settings.module.client_portal',
} as const

// User preference context for overriding org settings
export const USER_PREFERENCE_CONTEXT = {
  TIME_FORMAT: 'time_format',
  DATE_FORMAT: 'date_format',
  TIMEZONE: 'timezone',
  LANGUAGE: 'language',
} as const

// Helper to merge user preferences with org settings
export function getEffectiveSettings(
  orgSettings: OrganizationSettings,
  userPreferences?: Partial<OrganizationGeneralSettings>,
): OrganizationSettings {
  return {
    ...orgSettings,
    general: {
      ...orgSettings.general,
      ...userPreferences,
    },
  }
}
